import { priceBandFor } from './priceBand.js';

/**
 * The "Price" filter — a price band, derived, for the filter pickers and nothing else.
 *
 *   view configs persist  'price_band'             (a filter's `columnId`, a pinned chip's name)
 *   display name          "Price"
 *   read it with          priceBandWord(node)
 *   guard a field ref     isPriceBandField(ref)
 *
 * WHY A DERIVED FIELD. Price is a canonical composite (migration 20260926170000): `price_label`
 * holds the words, `price_amount` the number. Neither filters the way a person asks. The label is
 * free text, and the amount is null for "Free", donation and pay-what-you-can, so a filter on it
 * cannot find the free events, which are the ones people most often want. The band is the micro
 * app's answer (src/utils/priceBand.js): the amount when there is one, else a number read out of
 * the label, so "Free" is 0. That rule already runs on the server for the space chat, and this is
 * it reaching the filter chips.
 *
 * FILTER ONLY, for the reasons utils/collectionField.js gives for Is Collection: there is no
 * column behind it, so it is kept off the Edit tab, the Info tab, field creation and the Grid.
 *
 * An event with no readable price has no band. It matches no band and reads as empty, the same
 * way the micro app shows it only when no band is chosen.
 */

export const PRICE_BAND_FIELD_KEY = 'price_band';
export const PRICE_BAND_FIELD_LABEL = 'Price';

/** The bands, in the micro app's order. The word is the value a filter stores and compares. */
export const PRICE_BAND_WORDS = Object.freeze({ free: 'Free', under25: 'Under $25', over25: 'Over $25' });

/** Picker-shaped descriptor: a single select whose options are the three bands. */
export const PRICE_BAND_FIELD_DEF = Object.freeze({
  id: PRICE_BAND_FIELD_KEY,
  name: PRICE_BAND_FIELD_KEY,
  label: PRICE_BAND_FIELD_LABEL,
  type: 'singleSelect',
  source: 'internal',
  synthetic: true,
  readOnly: true,
  options: { choices: Object.values(PRICE_BAND_WORDS).map((name) => ({ name })) },
});

/**
 * Column-shaped descriptor for the Grid/List inline filter row, which builds its dropdown from
 * columns — the same reason IS_COLLECTION_FILTER_COLUMN exists. Never a Grid column.
 */
export const PRICE_BAND_FILTER_COLUMN = Object.freeze({
  id: PRICE_BAND_FIELD_KEY,
  label: PRICE_BAND_FIELD_LABEL,
  type: 'singleSelect',
  readOnly: true,
  options: PRICE_BAND_FIELD_DEF.options,
  accessor: (row) => priceBandWord(row),
});

/** True when a persisted field reference points at this field. Key only, never a label. */
export function isPriceBandField(fieldRef) {
  return fieldRef === PRICE_BAND_FIELD_KEY;
}

/**
 * The node's band as its word, or null when it has no readable price. Reads both halves of a
 * graph node, since a canonical key can sit in either depending on how the item was loaded.
 *
 * @param {object} node  a graph node (`_rawFields`, `customFields`)
 * @returns {string|null}
 */
export function priceBandWord(node) {
  const cf = { ...(node?.customFields || {}), ...(node?._rawFields || {}) };
  const band = priceBandFor(cf);
  return band ? PRICE_BAND_WORDS[band] : null;
}
